import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Paper,
  TextField,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  FormHelperText,
  Stack,
  Alert,
  InputAdornment
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { RecursoFinanceiro } from 'stores/cifa/cifa.store';
import { useNavigate, useLocation } from 'react-router-dom';
import { ROUTES } from 'routes/constants';
import {
  formatarValorParaExibicao,
  processarEntradaValor,
  getRecursoKey,
  validateRecursos,
  computeResumoFinanceiro,
} from './step1.helpers';

interface ErrorState {
  [key: string]: {
    tipo?: string;
    descricao?: string;
    valor_orcado?: string;
    valor_liquidado?: string;
  };
}

interface Step1Props {
  initialData: {
    recursos: RecursoFinanceiro[];
    rcl: number;
  };
  onNext: (data: {
    recursos: RecursoFinanceiro[];
    rcl: number;
  }) => void;
  loading: boolean;
}

const Step1: React.FC<Step1Props> = ({ initialData, onNext, loading }) => {
  const [itens, setItens] = useState<RecursoFinanceiro[]>(initialData.recursos);
  const [rcl, setRcl] = useState<number>(initialData.rcl);
  const [errors, setErrors] = useState<ErrorState>({});
  const [showEmptyAlert, setShowEmptyAlert] = useState(false);

  useEffect(() => {
    setItens(initialData.recursos);
    setRcl(initialData.rcl);
  }, [initialData]);

  const navigate = useNavigate();
  const location = useLocation();

  // Extrai o ano da rota, ex: /service_management/cifa-2027 => 2027
  const match = /cifa-(\d{4})/.exec(location.pathname);
  const anoReferencia = match ? Number(match[1]) : 2026;

  const handleAddItem = () => {
    setItens([
      ...itens,
      {
        tipo: 'Programa',
        descricao: '',
        valor_orcado: 0,
        valor_liquidado: 0,
      }
    ]);
    setShowEmptyAlert(false);
  };

  const handleRemoveItem = (index: number) => {
    const newItens = [...itens];
    newItens.splice(index, 1);
    setItens(newItens);

    const newErrors = { ...errors };
    delete newErrors[index];
    setErrors(newErrors);
  };

  const handleItemChange = (
    index: number,
    field: keyof RecursoFinanceiro,
    value: string | number
  ) => {
    const newItens = [...itens];
    newItens[index] = {
      ...newItens[index],
      [field]: (() => {
        if (field === 'valor_orcado' || field === 'valor_liquidado') {
          return typeof value === 'string' ? processarEntradaValor(value) : value;
        }
        return value;
      })()
    };

    setItens(newItens);

    if (errors[index]) {
      const newErrors = { ...errors };
      delete newErrors[index][field as keyof typeof newErrors[number]];
      if (Object.keys(newErrors[index]).length === 0) {
        delete newErrors[index];
      }
      setErrors(newErrors);
    }
  };

  const handleRclChange = (value: string) => {
    setRcl(processarEntradaValor(value));
    if (errors.rcl) {
      const newErrors = { ...errors };
      delete newErrors.rcl;
      setErrors(newErrors);
    }
  };

  const validate = (): boolean => {
    const result = validateRecursos(itens, rcl);
    setErrors(result.errors);
    if (result.showEmptyAlert) setShowEmptyAlert(true);
    return result.ok;
  };

  const handleSubmit = () => {
    if (validate()) {
      onNext({
        recursos: itens,
        rcl: rcl
      });
    }
  };

  const handleBack = () => {
    navigate(ROUTES[`INCLUDE_CIFA_${anoReferencia}` as keyof typeof ROUTES] as string);
  };

  const resumoFinanceiro = useMemo(() => computeResumoFinanceiro(itens, rcl), [itens, rcl]);



  return (
    <Stack marginX={{ xs: '1rem', md: '4rem' }} spacing='2rem' maxWidth='1152px' width='100%'>
      <Box display='flex' justifyContent='space-between' alignItems='center'>
        <Typography variant="h5" fontWeight="bold">
          Recursos Financeiros Aplicados
        </Typography>
        <Button
          variant="contained"
          onClick={handleAddItem}
          startIcon={<AddIcon />}
          disabled={loading}
        >
          Adicionar Item
        </Button>
      </Box>

      {showEmptyAlert && (
        <Alert severity="error" onClose={() => setShowEmptyAlert(false)}>
          Adicione pelo menos um item para continuar
        </Alert>
      )}

      <Stack spacing={3}>
        {itens.map((item, index) => (
          <Paper
            key={getRecursoKey(item, index)}
            elevation={2}
            sx={{
              p: 2,
              border: errors[index] ? '1px solid red' : '1px solid rgba(0, 0, 0, 0.12)',
              position: 'relative'
            }}
          >
            <Grid container spacing={2} alignItems="center" wrap="nowrap">
              <Grid item sx={{ flexGrow: 1, minWidth: 150 }}>
                <FormControl fullWidth size="small" error={!!errors[index]?.tipo}>
                  <InputLabel>Tipo</InputLabel>
                  <Select
                    label="Tipo"
                    value={item.tipo}
                    onChange={(e) => handleItemChange(index, 'tipo', e.target.value)}
                    sx={{ height: '40px' }}
                  >
                    <MenuItem value="Programa">Programa</MenuItem>
                    <MenuItem value="Projeto">Projeto</MenuItem>
                    <MenuItem value="Ação/Atividade">Ação/Atividade</MenuItem>
                  </Select>
                  <FormHelperText>{errors[index]?.tipo}</FormHelperText>
                </FormControl>
              </Grid>

              <Grid item sx={{ flexGrow: 2, minWidth: 200 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Descrição"
                  value={item.descricao}
                  onChange={(e) => handleItemChange(index, 'descricao', e.target.value.toLocaleUpperCase())}
                  error={!!errors[index]?.descricao}
                  helperText={errors[index]?.descricao}
                />
              </Grid>

              <Grid item sx={{ flexGrow: 1, minWidth: 150 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Valor Orçado"
                  value={formatarValorParaExibicao(item.valor_orcado)}
                  onChange={(e) => handleItemChange(index, 'valor_orcado', e.target.value)}
                  error={!!errors[index]?.valor_orcado}
                  helperText={errors[index]?.valor_orcado}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item sx={{ flexGrow: 1, minWidth: 150 }}>
                <TextField
                  fullWidth
                  size="small"
                  label="Valor Liquidado"
                  value={formatarValorParaExibicao(item.valor_liquidado)}
                  onChange={(e) => handleItemChange(index, 'valor_liquidado', e.target.value)}
                  error={!!errors[index]?.valor_liquidado}
                  helperText={errors[index]?.valor_liquidado}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  }}
                />
              </Grid>

              <Grid item>
                <IconButton
                  color="error"
                  onClick={() => handleRemoveItem(index)}
                  disabled={loading}
                >
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Paper>
        ))}
      </Stack>

      <Paper elevation={2} sx={{ p: 2 }}>
        <Typography variant="body1" fontWeight="bold" mb={1}>
          Qual o valor total da Receita Corrente Líquida do município no ano de 2025?
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={formatarValorParaExibicao(rcl)}
          onChange={(e) => handleRclChange(e.target.value)}
          error={!!errors.rcl}
          helperText={errors.rcl as string}
          InputProps={{
            startAdornment: <InputAdornment position="start">R$</InputAdornment>,
          }}
        />
      </Paper>

      <Paper elevation={2} sx={{ p: 2 }}>
        <Typography variant="h6" mb={2}>Resumo Financeiro</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, backgroundColor: '#e3f2fd', textAlign: 'center' }}>
              <Typography variant="body2">Total Orçado</Typography>
              <Typography variant="h6" fontWeight="bold">
                R$ {formatarValorParaExibicao(resumoFinanceiro.totalOrcado)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, backgroundColor: '#e8f5e9', textAlign: 'center' }}>
              <Typography variant="body2">Total Liquidado</Typography>
              <Typography variant="h6" fontWeight="bold">
                R$ {formatarValorParaExibicao(resumoFinanceiro.totalLiquidado)}
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, backgroundColor: '#fff3e0', textAlign: 'center' }}>
              <Typography variant="body2">% Execução</Typography>
              <Typography variant="h6" fontWeight="bold">
                {resumoFinanceiro.percentualExecucao}%
              </Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2, backgroundColor: '#f3e5f5', textAlign: 'center' }}>
              <Typography variant="body2">% Investido da RCL</Typography>
              <Typography variant="h6" fontWeight="bold">
                {resumoFinanceiro.percentualInvestidoRCL}%
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Paper>

      <Box display="flex" justifyContent="space-between" mt={4}>
        <Button
          variant="outlined"
          onClick={handleBack}
          disabled={loading}
          sx={{ minWidth: 200 }}
        >
          Voltar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={itens.length === 0 || loading}
          sx={{ minWidth: 200 }}
        >
          {loading ? 'Salvando...' : 'Salvar e Avançar'}
        </Button>
      </Box>
    </Stack>
  );
};

export default Step1;
